import dynamic from 'next/dynamic'
import {CustomerStoryContentSliceV2} from 'prismicio-types'
import {Container} from 'src/common/Container'
import {Sidebar} from './Sidebar/Sidebar'
import {isFilled} from '@prismicio/client'
import {CustomerStorySliceZoneContext} from 'src/CustomerStory'
import {useEffect, useRef, useState} from 'react'

const TableComponent = dynamic(() => import('./TableComponent').then((mod) => mod.TableComponent), {ssr: false})
const Quote = dynamic(() => import('./Quote').then((mod) => mod.Quote), {ssr: false})
const ProductSpotlight = dynamic(() => import('./ProductSpotlight').then((mod) => mod.ProductSpotlight), {ssr: false})
const RTEComponent = dynamic(() => import('./RTEComponent').then((mod) => mod.RTEComponent), {ssr: false})

type CustomerStoryContentV2Props = {
  slice: CustomerStoryContentSliceV2
  context: CustomerStorySliceZoneContext
}

export const CustomerStoryContentV2 = ({slice, context}: CustomerStoryContentV2Props) => {
  const containerRef = useRef<HTMLElement | null>(null)
  const [shouldMountTable, setShouldMountTable] = useState(false)

  // Get table container element
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const container = document.getElementById('table-container')

      if (container) {
        containerRef.current = container
        setShouldMountTable(true)
        observer.disconnect()
      }
    })

    observer.observe(document.body, {childList: true, subtree: true})

    // Cleanup observer on unmount
    return () => observer.disconnect()
  }, [])

  return (
    <Container className="overflow-visible !pt-0">
      <div className="flex w-full max-w-[1200px] flex-col justify-center gap-12 lg:flex-row lg:px-8 lg:pb-18 lg:pt-12 xl:px-0">
        <div className="w-full max-w-[600px] px-5 max-lg:order-2 max-lg:mx-auto md:px-0 lg:max-w-[680px]">
          <RTEComponent field={slice.primary.content} />
          {shouldMountTable && <TableComponent content={slice.primary.before_and_after_table} />}
          {shouldMountTable && slice.primary.quotes.map((quote, index) => <Quote key={index} quote={quote} />)}
          {shouldMountTable &&
            slice.primary.product_spotlight.map((product, index) => (
              <ProductSpotlight key={index} product_spotlight={product} />
            ))}
        </div>
        {isFilled.group(slice.primary.summary_section) && (
          <Sidebar
            companyLogo={slice.primary.summary_section[0]?.company_icon}
            cta={slice.primary.cta[0]}
            summary={slice.primary.summary_section[0]?.description}
            products={slice.primary.products_used}
            addons={slice.primary.add_ons}
            category={context.category}
            location={context.location}
            headcount={context.headcount}
          />
        )}
      </div>
    </Container>
  )
}
